// 性能优化工具集合

// 图片懒加载指令
export const lazyLoad = {
  install(Vue) {
    Vue.directive('lazy', {
      inserted(el, binding) {
        const img = el
        const src = binding.value

        // 创建交叉观察器
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              img.src = src
              img.classList.add('fade-in')
              observer.unobserve(img)
            }
          })
        }, {
          threshold: 0.1
        })

        observer.observe(img)
      }
    })
  }
}

// 防抖函数
export function debounce(func, wait, immediate = false) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      timeout = null
      if (!immediate) func.apply(this, args)
    }
    const callNow = immediate && !timeout
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
    if (callNow) func.apply(this, args)
  }
}

// 节流函数
export function throttle(func, wait) {
  let inThrottle
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, wait)
    }
  }
}

// 虚拟滚动指令
export const virtualScroll = {
  install(Vue) {
    Vue.directive('virtual-scroll', {
      bind(el, binding) {
        const { itemHeight = 50, bufferSize = 10 } = binding.value
        
        el.style.overflow = 'auto'
        el.style.height = '100%'
        
        const virtualScrollHandler = throttle(() => {
          const scrollTop = el.scrollTop
          const containerHeight = el.clientHeight
          
          const startIndex = Math.floor(scrollTop / itemHeight)
          const endIndex = Math.min(
            startIndex + Math.ceil(containerHeight / itemHeight) + bufferSize,
            binding.value.items.length
          )
          
          // 触发更新事件
          if (binding.value.onUpdate) {
            binding.value.onUpdate({
              startIndex: Math.max(0, startIndex - bufferSize),
              endIndex,
              scrollTop
            })
          }
        }, 16)
        
        el.addEventListener('scroll', virtualScrollHandler)
        el._virtualScrollHandler = virtualScrollHandler
      },
      unbind(el) {
        if (el._virtualScrollHandler) {
          el.removeEventListener('scroll', el._virtualScrollHandler)
        }
      }
    })
  }
}

// 预加载资源
export function preloadResource(url, type = 'image') {
  return new Promise((resolve, reject) => {
    if (type === 'image') {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = reject
      img.src = url
    } else if (type === 'script') {
      const script = document.createElement('script')
      script.onload = resolve
      script.onerror = reject
      script.src = url
      document.head.appendChild(script)
    } else if (type === 'style') {
      const link = document.createElement('link')
      link.rel = 'stylesheet'
      link.onload = resolve
      link.onerror = reject
      link.href = url
      document.head.appendChild(link)
    }
  })
}

// 批量预加载
export function preloadResources(resources) {
  const promises = resources.map(resource => {
    if (typeof resource === 'string') {
      return preloadResource(resource)
    } else {
      return preloadResource(resource.url, resource.type)
    }
  })
  
  return Promise.all(promises)
}

// 内存缓存
export class MemoryCache {
  constructor(maxSize = 100) {
    this.cache = new Map()
    this.maxSize = maxSize
  }
  
  get(key) {
    if (this.cache.has(key)) {
      // 移到最后（LRU）
      const value = this.cache.get(key)
      this.cache.delete(key)
      this.cache.set(key, value)
      return value
    }
    return null
  }
  
  set(key, value) {
    if (this.cache.has(key)) {
      this.cache.delete(key)
    } else if (this.cache.size >= this.maxSize) {
      // 删除最旧的项
      const firstKey = this.cache.keys().next().value
      this.cache.delete(firstKey)
    }
    this.cache.set(key, value)
  }
  
  clear() {
    this.cache.clear()
  }
}

// 创建全局缓存实例
export const globalCache = new MemoryCache()

// 请求去重
export class RequestDeduplicator {
  constructor() {
    this.pendingRequests = new Map()
  }
  
  async request(key, requestFn) {
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)
    }
    
    const promise = requestFn()
    this.pendingRequests.set(key, promise)
    
    try {
      const result = await promise
      return result
    } finally {
      this.pendingRequests.delete(key)
    }
  }
}

// 全局请求去重器
export const requestDeduplicator = new RequestDeduplicator()

// 组件懒加载高阶组件
export function lazyComponent(importFunc, fallbackComponent = null) {
  return () => ({
    component: importFunc(),
    loading: fallbackComponent || {
      template: '<div class="loading">加载中...</div>'
    },
    error: {
      template: '<div class="error">加载失败</div>'
    },
    delay: 200,
    timeout: 10000
  })
}

// 性能监控混入
export const performanceMixin = {
  beforeCreate() {
    // 只在开发环境或明确启用性能监控时记录
    if (process.env.NODE_ENV === 'development' && window.ENABLE_PERFORMANCE_MONITOR) {
      this.$componentStartTime = performance.now()
    }
  },
  mounted() {
    // 只在开发环境或明确启用性能监控时记录
    if (process.env.NODE_ENV === 'development' && window.ENABLE_PERFORMANCE_MONITOR) {
      const endTime = performance.now()
      const duration = endTime - this.$componentStartTime
      
      // 提高阈值到200ms，减少日志输出
      // 过滤掉Element UI组件（这些组件渲染慢是正常的）
      const componentName = this.$options.name || ''
      const isElementComponent = componentName.startsWith('El') || componentName === 'transition'
      
      if (duration > 200 && !isElementComponent) { // 超过200ms且不是Element UI组件才记录
        console.warn(`组件 ${componentName || '未命名'} 渲染耗时: ${duration.toFixed(2)}ms`)
      }
    }
  }
}

// 导出所有优化工具
export default {
  install(Vue) {
    Vue.use(lazyLoad)
    Vue.use(virtualScroll)
    Vue.mixin(performanceMixin)
    
    // 添加全局方法
    Vue.prototype.$debounce = debounce
    Vue.prototype.$throttle = throttle
    Vue.prototype.$preload = preloadResource
    Vue.prototype.$cache = globalCache
    Vue.prototype.$requestDedup = requestDeduplicator
  }
} 